import { useEffect, useId, useState } from 'react';
import {
  Banner,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Form,
  FormattedRelativeTime,
  Heading,
  InlineStack,
  Label,
  Scrollable,
  Stack,
  Text,
  TextArea,
  TextField,
  TextLabel,
  useConfirmationModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { ServicesApiCopilotCron } from '@/services/api/services-api';
import { useServicesMutation } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { useQueryClient } from '@tanstack/react-query';
import { formatSchedule } from '../utils';

const MAX_PROMPT_LENGTH = 10_000;

interface RoutineDetailProps {
  routine: ServicesApiCopilotCron;
  workspaceId: number;
}

export function RoutineDetail({ routine, workspaceId }: RoutineDetailProps) {
  const queryClient = useQueryClient();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const formId = useId();

  const [name, setName] = useState(routine.name);
  const [prompt, setPrompt] = useState(routine.prompt);
  const [isEditing, setIsEditing] = useState(false);

  // Sync form state when routine data changes (e.g. after a mutation)
  useEffect(() => {
    if (!isEditing) {
      setName(routine.name);
      setPrompt(routine.prompt);
    }
  }, [routine.name, routine.prompt, isEditing]);

  const deleteMutation = useServicesMutation('deleteCopilotCron', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['listCopilotCrons'] });
      track('aiAssistant', 'routineDeleted', {});
    },
    onError: () => {
      snackbars.create({
        variant: 'error',
        message: 'Failed to delete routine. Please try again.',
      });
    },
  });

  const saveMutation = useServicesMutation('updateCopilotCron', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['listCopilotCrons'] });
      setIsEditing(false);
      track('aiAssistant', 'routineSaved', {});
      snackbars.create({
        variant: 'success',
        message: 'Routine updated.',
      });
    },
    onError: () => {
      snackbars.create({
        variant: 'error',
        message: 'Failed to update routine. Please try again.',
      });
    },
  });

  const togglePauseMutation = useServicesMutation('updateCopilotCron', {
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['listCopilotCrons'] });
      track('aiAssistant', variables.body.paused === true ? 'routinePaused' : 'routineResumed', {});
    },
    onError: () => {
      snackbars.create({
        variant: 'error',
        message: 'Failed to update routine. Please try again.',
      });
    },
  });

  const isMutating = deleteMutation.isPending || saveMutation.isPending || togglePauseMutation.isPending;

  const handleDelete = () => {
    confirm({
      title: 'Delete routine',
      message: 'Are you sure you want to delete this routine? This action cannot be undone.',
      isDanger: true,
      onConfirm() {
        deleteMutation.mutate({
          params: { environment_id: workspaceId, cron_id: routine.id },
          body: {},
        });
      },
    });
  };

  const handleTogglePause = () => {
    togglePauseMutation.mutate({
      params: { environment_id: workspaceId, cron_id: routine.id },
      body: { paused: !routine.paused },
    });
  };

  const handleCancel = () => {
    setName(routine.name);
    setPrompt(routine.prompt);
    setIsEditing(false);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;

    saveMutation.mutate({
      params: { environment_id: workspaceId, cron_id: routine.id },
      body: {
        name: name !== routine.name ? name : undefined,
        prompt: prompt !== routine.prompt ? prompt : undefined,
      },
    });
  };

  const promptLength = prompt.length;
  const isPromptTooLong = promptLength > MAX_PROMPT_LENGTH;
  const isNameEmpty = name.trim().length === 0;
  const hasChanges = name !== routine.name || prompt !== routine.prompt;
  const canSave = hasChanges && !isPromptTooLong && !isNameEmpty && !saveMutation.isPending;

  const statusColor = routine.paused ? 'grey' : routine.last_status === 'error' ? 'red' : 'green';
  const statusLabel = routine.paused ? 'Paused' : routine.last_status === 'error' ? 'Error' : 'Active';
  const hasError = !routine.paused && routine.last_status === 'error' && routine.last_error != null;

  const schedule = formatSchedule(routine.schedule);
  const scheduleWithTimezone = routine.timezone !== 'UTC' ? `${schedule} (${routine.timezone})` : schedule;

  return (
    <Box display="flex" flexDirection="column" gap="300" w="full" flexGrow="1" minH="0" style={{ maxWidth: '600px' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <InlineStack gap="100" alignItems="center">
          <Heading level="4">{routine.name}</Heading>
          <Label showStatusIndicator={true} color={statusColor}>
            {statusLabel}
          </Label>
        </InlineStack>
        <InlineStack gap="100">
          {!isEditing ? (
            <Button
              variant="subtle"
              size="sm"
              icon="edit"
              onClick={() => {
                track('aiAssistant', 'routineEditClicked', {});
                setIsEditing(true);
              }}
            >
              Edit
            </Button>
          ) : null}
          <Button variant="subtle" size="sm" onClick={handleTogglePause} isDisabled={isMutating}>
            {routine.paused ? 'Resume' : 'Pause'}
          </Button>
          <Button variant="subtle" size="sm" isDanger={true} onClick={handleDelete} isDisabled={isMutating}>
            Delete
          </Button>
        </InlineStack>
      </Box>

      {hasError ? (
        <Banner variant="error" title="Last run failed">
          {routine.last_error}
        </Banner>
      ) : null}

      {/* Metadata */}
      <InlineStack gap="400">
        <Stack gap="025">
          <TextLabel as="div">Schedule</TextLabel>
          <Text size="s">{scheduleWithTimezone}</Text>
        </Stack>
        {routine.next_run_at != null ? (
          <Stack gap="025">
            <TextLabel as="div">Next run</TextLabel>
            <Text size="s">
              <FormattedRelativeTime date={routine.next_run_at} withTooltip={true} shouldUppercaseFirst={false} />
            </Text>
          </Stack>
        ) : null}
        {routine.last_run_at != null ? (
          <Stack gap="025">
            <TextLabel as="div">Last run</TextLabel>
            <Text size="s">
              <FormattedRelativeTime date={routine.last_run_at} withTooltip={true} shouldUppercaseFirst={false} />
            </Text>
          </Stack>
        ) : null}
      </InlineStack>

      <Divider />

      {/* Content — view or edit mode */}
      {isEditing ? (
        <>
          <Scrollable overflow="y" withEdgeIndicators={true} flexGrow="1">
            <Form id={formId} onSubmit={handleSubmit}>
              <Stack gap="200">
                <TextField
                  label="Name"
                  value={name}
                  onInput={(event: Event & { target: HTMLInputElement }) => setName(event.target.value)}
                  isInvalid={isNameEmpty}
                  error={isNameEmpty ? 'Name is required' : undefined}
                />
                <Stack gap="050">
                  <TextArea
                    label="Prompt"
                    value={prompt}
                    onInput={(event: Event & { target: HTMLTextAreaElement }) => setPrompt(event.target.value)}
                    minRows={4}
                    maxRows={12}
                    isInvalid={isPromptTooLong}
                    error={
                      isPromptTooLong
                        ? `Prompt exceeds ${MAX_PROMPT_LENGTH.toLocaleString()} character limit`
                        : undefined
                    }
                    description="Tell the agent what to look for and include in the summary."
                  />
                  <Text size="xs" color={isPromptTooLong ? 'critical' : 'subtle'}>
                    {promptLength.toLocaleString()} / {MAX_PROMPT_LENGTH.toLocaleString()}
                  </Text>
                </Stack>
              </Stack>
            </Form>
          </Scrollable>
          <ButtonGroup>
            <Button
              variant="primary"
              type="submit"
              form={formId}
              isDisabled={!canSave}
              isLoading={saveMutation.isPending}
            >
              Save
            </Button>
            <Button variant="secondary" onClick={handleCancel} isDisabled={saveMutation.isPending}>
              Cancel
            </Button>
          </ButtonGroup>
        </>
      ) : (
        <Box display="flex" flexDirection="column" flexGrow="1" minH="0" gap="050">
          <TextLabel as="div">Prompt</TextLabel>
          <Scrollable overflow="y" withEdgeIndicators={true} flexGrow="1">
            <Text size="s" whiteSpace="pre-wrap">
              {routine.prompt}
            </Text>
          </Scrollable>
        </Box>
      )}
    </Box>
  );
}
